import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { MapPin, Plus, Trash2, Home, Briefcase, MoreHorizontal, Check, ShieldCheck, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { addressesAPI } from '../lib/api';
import { geocode } from '../lib/traveltime';

interface Address {
    _id: string;
    type: string;
    fullAddress: string;
    contactName: string;
    contactPhone: string;
    city: string;
    state: string;
    pincode: string;
    location: {
        type: string;
        coordinates: number[];
    };
    isDefault: boolean;
}

const Addresses = () => {
    const navigate = useNavigate();
    const [addresses, setAddresses] = useState<Address[]>([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        type: 'home',
        fullAddress: '',
        contactName: '',
        contactPhone: '',
        city: '',
        state: '',
        pincode: '',
        isDefault: false,
    });

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/login');
            return;
        }
        fetchAddresses();
    }, [navigate]);

    const fetchAddresses = async () => {
        try {
            const response = await addressesAPI.getAll();
            setAddresses(response.data);
        } catch (error) {
            console.error('Error fetching addresses:', error);
            toast.error('Failed to load addresses');
        } finally {
            setLoading(false);
        }
    };

    const handleAddAddress = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!formData.fullAddress.trim() || !formData.contactName.trim() || !formData.contactPhone.trim() || !formData.city.trim() || !formData.state.trim() || !formData.pincode.trim()) {
            toast.error('Please fill all required fields');
            return;
        }

        setSubmitting(true);
        try {
            let coordinates = [0, 0];
            try {
                // Auto-geocode address before saving
                const geocodeRes = await geocode(`${formData.fullAddress}, ${formData.city}, ${formData.state} ${formData.pincode}`);
                if (geocodeRes.features?.[0]?.geometry?.coordinates) {
                    coordinates = geocodeRes.features[0].geometry.coordinates; // [lng, lat]
                }
            } catch (err) {
                console.error('Geocoding failed during address creation:', err);
            }

            const data = {
                ...formData,
                location: {
                    type: 'Point',
                    coordinates: coordinates,
                }
            };
            await addressesAPI.create(data);
            toast.success('Address added successfully');
            setShowForm(false);
            setFormData({
                type: 'home',
                fullAddress: '',
                contactName: '',
                contactPhone: '',
                city: '',
                state: '',
                pincode: '',
                isDefault: false,
            });
            fetchAddresses();
        } catch (error) {
            console.error('Error adding address:', error);
            toast.error('Failed to add address');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this address?')) return;

        try {
            await addressesAPI.delete(id);
            toast.success('Address deleted');
            fetchAddresses();
        } catch (error) {
            toast.error('Failed to delete address');
        }
    };

    const handleSetDefault = async (id: string) => {
        try {
            await addressesAPI.setDefault(id);
            toast.success('Default address updated');
            fetchAddresses();
        } catch (error) {
            toast.error('Failed to update default address');
        }
    };

    const getIcon = (type: string) => {
        switch (type.toLowerCase()) {
            case 'home': return <Home size={20} />;
            case 'work': return <Briefcase size={20} />;
            default: return <MoreHorizontal size={20} />;
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#10b981]"></div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="max-w-4xl mx-auto">
                <div className="mb-6 flex justify-between items-center">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800">My Addresses</h1>
                        <p className="text-gray-500">Manage your delivery locations</p>
                    </div>
                    {!showForm && (
                        <button
                            onClick={() => setShowForm(true)}
                            className="bg-[#10b981] text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-[#059669] transition-colors shadow-sm"
                        >
                            <Plus size={20} />
                            Add New Address
                        </button>
                    )}
                </div>

                {/* Add Modal */}
                {showForm && (
                    <div className="modal-overlay backdrop-blur-sm">
                        <div className="modal-content max-w-2xl">
                            <div className="flex items-center justify-between p-6 border-b border-gray-100">
                                <h2 className="text-xl font-bold text-gray-900">Add New Address</h2>
                                <button onClick={() => setShowForm(false)} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-400">
                                    <X size={24} />
                                </button>
                            </div>

                            <form id="address-form" onSubmit={handleAddAddress} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {/* Type Selection */}
                                    <div className="md:col-span-2">
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Address Type</label>
                                        <div className="flex gap-4">
                                            {['home', 'work', 'other'].map((t) => (
                                                <button
                                                    key={t}
                                                    type="button"
                                                    onClick={() => setFormData({ ...formData, type: t })}
                                                    className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl border transition-all ${formData.type === t
                                                        ? 'border-[#10b981] bg-green-50/50 text-[#10b981] ring-1 ring-[#10b981]'
                                                        : 'border-gray-200 bg-gray-50 text-gray-500 hover:bg-white hover:border-[#10b981]'
                                                        }`}
                                                >
                                                    {getIcon(t)}
                                                    <span className="capitalize font-bold">{t}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Contact Details */}
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Contact Name *</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.contactName}
                                            onChange={(e) => setFormData({ ...formData, contactName: e.target.value })}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                            placeholder="Receiver's name"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Contact Phone *</label>
                                        <input
                                            type="tel"
                                            required
                                            value={formData.contactPhone}
                                            onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                            placeholder="10-digit mobile number"
                                        />
                                    </div>

                                    {/* Address Details */}
                                    <div className="md:col-span-2">
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Full Address *</label>
                                        <textarea
                                            required
                                            value={formData.fullAddress}
                                            onChange={(e) => setFormData({ ...formData, fullAddress: e.target.value })}
                                            className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all h-24 resize-none"
                                            placeholder="House No., Building, Street, Area..."
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">City *</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.city}
                                            onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                                            className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                        />
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">State *</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.state}
                                                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-xs font-bold text-gray-400 uppercase tracking-widest mb-1.5">Pincode *</label>
                                            <input
                                                type="text"
                                                required
                                                value={formData.pincode}
                                                onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                                                className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#10b981] focus:bg-white outline-none transition-all"
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2 pt-2">
                                    <input
                                        type="checkbox"
                                        id="isDefault"
                                        checked={formData.isDefault}
                                        onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                                        className="w-4 h-4 text-[#10b981] border-gray-300 rounded focus:ring-[#10b981]"
                                    />
                                    <label htmlFor="isDefault" className="text-sm font-bold text-gray-700">Set as default delivery address</label>
                                </div>
                            </form>

                            <div className="p-6 border-t border-gray-100 bg-gray-50 flex gap-4">
                                <button
                                    type="button"
                                    onClick={() => setShowForm(false)}
                                    className="flex-1 py-3 bg-white border border-gray-200 text-gray-700 rounded-xl font-bold hover:bg-gray-100 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    form="address-form"
                                    disabled={submitting}
                                    className="flex-1 py-3 bg-[#10b981] text-white rounded-xl font-bold hover:bg-[#059669] transition-all shadow-lg shadow-[#10b981]/20 disabled:opacity-50"
                                >
                                    {submitting ? 'Saving...' : 'Save Address'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Addresses List */}
                {addresses.length === 0 ? (
                    <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-gray-200">
                        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                            <MapPin className="text-gray-300" size={40} />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">No saved addresses</h3>
                        <p className="text-gray-500 mb-6">Add a delivery address to start ordering</p>
                        <button
                            onClick={() => setShowForm(true)}
                            className="bg-[#10b981] text-white px-8 py-3 rounded-xl font-bold hover:bg-[#059669] transition-colors shadow-sm"
                        >
                            Add Your First Address
                        </button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-4">
                        {addresses.map((addr) => (
                            <div
                                key={addr._id}
                                className={`bg-white rounded-2xl p-5 border transition-all hover:shadow-md flex items-start justify-between ${addr.isDefault ? 'border-[#10b981] ring-1 ring-[#10b981]/10' : 'border-gray-100'
                                    }`}
                            >
                                <div className="flex items-start gap-4">
                                    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${addr.isDefault ? 'bg-[#10b981] text-white' : 'bg-gray-50 text-gray-400'
                                        }`}>
                                        {getIcon(addr.type)}
                                    </div>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-3 mb-1">
                                            <h3 className="font-bold text-gray-800 capitalize">{addr.type}</h3>
                                            {addr.isDefault && (
                                                <span className="flex items-center gap-1 px-2 py-0.5 bg-[#10b981]/10 text-[#10b981] text-[10px] font-bold uppercase tracking-wider rounded-full">
                                                    <ShieldCheck size={12} /> Default
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm font-medium text-gray-700 mb-1">{addr.contactName} • {addr.contactPhone}</p>
                                        <p className="text-sm text-gray-500 leading-relaxed">
                                            {addr.fullAddress}, {addr.city}, {addr.state} - {addr.pincode}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex flex-col items-end gap-3 ml-4">
                                    <div className="flex items-center gap-2">
                                        {!addr.isDefault && (
                                            <button
                                                onClick={() => handleSetDefault(addr._id)}
                                                className="text-xs font-bold text-[#10b981] hover:text-[#059669] px-3 py-1.5 rounded-lg hover:bg-[#10b981]/5 transition-colors"
                                            >
                                                Set Default
                                            </button>
                                        )}
                                        <button
                                            onClick={() => handleDelete(addr._id)}
                                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-all"
                                            title="Delete address"
                                        >
                                            <Trash2 size={18} />
                                        </button>
                                    </div>
                                    {addr.isDefault && (
                                        <div className="text-[#10b981] flex items-center gap-1">
                                            <Check size={16} />
                                            <span className="text-xs font-bold">Selected</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </Layout>
    );
};

export default Addresses;
